import { CertificateService } from "@root/services/certificate";
import { useState } from "react";
import useLanguage from "@root/hooks/useLanguage";
import { RECORDS_PER_PAGE } from "@root/constants";

export function useCertificate() {
  const certificateService = new CertificateService();
  const { currentLanguage } = useLanguage();
  const [loadingCourses, setLoadingCourses] = useState<Set<string>>(new Set());
  const [loadingCertificates, setLoadingCertificates] = useState<Set<string>>(new Set());
  const [csvLoadingCourses, setCsvLoadingCourses] = useState<Set<string>>(new Set());
  const [aLlCertificatesLoading, setALlCertificatesLoading] = useState(false);
  const [allCertificatesData, setAllCertificatesData] = useState([]);
  const [multiCertificatesLoading, setMultiCertificatesLoading] =
    useState(false);
  const [certificatePageNo, setCertificatePageNo] = useState(1);
  const [certificatePageCount, setCertificatePageCount] = useState(0);

  const fetchCertificate = async (
    payload: any,
    fileName = "KPEC 저널리즘 교육 이수증.pdf"
  ) => {
    const { courseId, journalistId, userId } = payload;
    if (!courseId) return;

    // Create unique identifier for this specific user's certificate
    const certificateId = `${courseId}-${journalistId || userId || 'unknown'}`;
    
    setLoadingCertificates((prev) => new Set(prev).add(certificateId));
    try {
      const response = await certificateService.fetchCertificate(payload, currentLanguage);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoadingCertificates((prev) => {
        const newSet = new Set(prev);
        newSet.delete(certificateId);
        return newSet;
      });
    }
  };

  const fetchUserInCsv = async (
    payload: any,
    fileName?: string
  ) => {
    const courseId = payload.courseId;
    if (!courseId) return;

    // Set fileName based on language if not provided
    const finalFileName = fileName || (currentLanguage === "EN" ? "All Enrolled Users.xlsx" : "KPEC 저널리즘 교육 현황.xlsx");

    setCsvLoadingCourses((prev) => new Set(prev).add(courseId));
    try {
      const response = await certificateService.fetchUsersInCsv(payload);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", finalFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setCsvLoadingCourses((prev) => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }
  };

  const fetchMultiCertificates = async (
    payload: any = {},
    fileName = "KPEC 저널리즘 교육 그룹 이수증.pdf"
  ) => {
    setMultiCertificatesLoading(true);
    try {
      const response = await certificateService.fetchCertificates(payload, currentLanguage);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setMultiCertificatesLoading(false);
    }
  };

  const fetchALlJCertificates = async (payload: any = {}, page?: number) => {
    setALlCertificatesLoading(true);
    try {
      const currentPage = page !== undefined ? page : certificatePageNo;
      const limit = RECORDS_PER_PAGE;
      const offset = (currentPage - 1) * limit;
      
      const payloadWithPagination = {
        ...payload,
        limit,
        offset,
      };
      
      const response = await certificateService.fetchAllCertificates(payloadWithPagination);
      const data = response?.data?.data || { rows: [], count: 0 };
      setAllCertificatesData(data);
      setCertificatePageCount(data?.count || 0);
    } finally {
      setALlCertificatesLoading(false);
    }
  };

  const isCourseLoading = (courseId: string) => {
    return loadingCourses.has(courseId);
  };

  const isCsvLoading = (courseId: string) => {
    return csvLoadingCourses.has(courseId);
  };

  const isCertificateLoading = (courseId: string, journalistId?: string | number, userId?: string | number) => {
    const certificateId = `${courseId}-${journalistId || userId || 'unknown'}`;
    return loadingCertificates.has(certificateId);
  };

  return {
    certificateLoading: loadingCourses.size > 0, // Keep for backward compatibility
    loadingCourses,
    isCourseLoading,
    isCsvLoading,
    isCertificateLoading,
    fetchCertificate,
    fetchMultiCertificates,
    multiCertificatesLoading,
    aLlCertificatesLoading,
    allCertificatesData,
    fetchALlJCertificates,
    fetchUserInCsv,
    certificatePageNo,
    setCertificatePageNo,
    certificatePageCount,
  };
}

export default useCertificate;
